import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Users,
  Upload,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Home,
  Calendar,
  Flame,
  Euro,
  UserCheck,
  MessageSquare,
  Edit2,
  Trash2,
  X,
  Save,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  PhoneOff,
  CalendarCheck,
  UserX,
  AlertTriangle,
  Filter,
  ChevronDown,
  CheckSquare,
  Square,
  UserPlus,
  Flag
} from "lucide-react";
import { getUserRole, isTelepro, getCurrentUser, getUserCommissions } from "./Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SortTableHead, useSort } from "@/components/ui/sort-header";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const STATUS_CONFIG = {
  a_appeler: { label: "À appeler", icon: Phone, color: "text-blue-600", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  rappeler: { label: "À rappeler", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  ne_repond_pas: { label: "Ne répond pas", icon: PhoneOff, color: "text-gray-600", bg: "bg-muted/30", border: "border-gray-200" },
  rdv_pris: { label: "RDV pris", icon: CalendarCheck, color: "text-purple-600", bg: "bg-purple-500/10", border: "border-purple-200" },
  converti: { label: "Converti", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  refuse: { label: "Refusé", icon: XCircle, color: "text-red-600", bg: "bg-red-500/10", border: "border-red-500/30" },
  non_eligible: { label: "Non éligible", icon: UserX, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
};

// Helper: check if a date is within the last N days
function isWithinDays(dateStr, days) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  const diff = (now - d) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= days;
}

// Helper: check if a date is today
function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  return d.toDateString() === today.toDateString();
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // États pour la sélection et actions en masse
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Dynamic user list for attribution
  const [assignableUsers, setAssignableUsers] = useState([]);
  
  // Sort state
  const { sortField, sortDir, handleSort } = useSort("date_reception", "desc");
  
  // Rôle et utilisateur connecté
  const userRole = getUserRole();
  const userName = localStorage.getItem("label_energie_user") || "";
  const isUserTelepro = isTelepro();
  const canUseBulkActions = userRole === "admin_full" || userRole === "admin_restricted";
  const canSeeAttribution = canUseBulkActions || userRole === "administratif";

  const [formData, setFormData] = useState({
    date_reception: "",
    nom: "",
    prenom: "",
    ville: "",
    code_postal: "",
    telephone: "",
    email: "",
    logement: "",
    anciennete_logement: "",
    systeme_chauffage: "",
    revenu_fiscal: "",
    personnes_foyer: "",
    commentaire: "",
    commentaire_mendy: "",
    statut: "a_appeler"
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchLeads();
  }, [statusFilter]);
  
  // Fetch dynamic assignable users (all active users from DB)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/users`);
        const users = (res.data || []).filter(u => u.active !== false && !u.username.startsWith("TEST_"));
        setAssignableUsers(users.map(u => u.username));
      } catch (err) {
        setAssignableUsers([]);
      }
    };
    fetchUsers();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchLeads = async () => {
    try {
      let params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.append("statut", statusFilter);
      }
      // Envoyer le nom d'utilisateur et le rôle pour le filtrage côté serveur
      if (userName) {
        params.append("user_name", userName);
      }
      if (userRole) {
        params.append("user_role", userRole);
      }
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/leads${queryString}`);
      setLeads(response.data.leads);
      setStats(response.data.stats);
      // Réinitialiser la sélection quand on recharge les données
      setSelectedLeads([]);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.csv') && !fileName.endsWith('.xls')) {
      toast.error("Format non supporté. Utilisez un fichier .xlsx ou .csv");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/leads/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchLeads();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreateLead = async () => {
    if (!formData.nom.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      // Auto-assign to current user if telepro
      const currentRole = localStorage.getItem("label_energie_role") || "";
      const currentUser = localStorage.getItem("label_energie_user") || "";
      if (currentRole === "telepro" || currentRole === "regie") {
        payload.telepro_assigne = currentUser;
        payload.attribue_a = currentUser;
      }
      const response = await axios.post(`${API}/leads`, payload);
      toast.success("Lead créé avec succès");
      setShowCreateDialog(false);
      resetForm();
      // Navigate to the new lead detail page
      navigate(`/leads/${response.data.id}`);
    } catch (error) {
      console.error("Error creating lead:", error);
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce lead ?")) return;

    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead supprimé");
      fetchLeads();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { statut: newStatus });
      toast.success("Statut mis à jour");
      fetchLeads();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // ==================== FONCTIONS DE SÉLECTION ET ACTIONS EN MASSE ====================
  
  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(lead => lead.id));
    }
  };

  const handleBulkAssign = async (teleproName) => {
    if (selectedLeads.length === 0) {
      toast.error("Aucun lead sélectionné");
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/leads/bulk-assign`, {
        lead_ids: selectedLeads,
        attribue_a: teleproName
      });
      toast.success(response.data.message);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      console.error("Error bulk assigning leads:", error);
      toast.error("Erreur lors de l'attribution en masse");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Aucun lead sélectionné");
      return;
    }
    
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${selectedLeads.length} lead(s) ?`)) return;
    
    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/leads/bulk-delete`, {
        lead_ids: selectedLeads
      });
      toast.success(response.data.message);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      console.error("Error bulk deleting leads:", error);
      toast.error("Erreur lors de la suppression en masse");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date_reception: "",
      nom: "",
      prenom: "",
      ville: "",
      code_postal: "",
      telephone: "",
      email: "",
      logement: "",
      anciennete_logement: "",
      systeme_chauffage: "",
      revenu_fiscal: "",
      personnes_foyer: "",
      commentaire: "",
      commentaire_mendy: "",
      statut: "a_appeler"
    });
  };

  // Filter leads by search term
  const searchFilteredLeads = leads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    return (
      lead.nom?.toLowerCase().includes(searchLower) ||
      lead.prenom?.toLowerCase().includes(searchLower) ||
      lead.ville?.toLowerCase().includes(searchLower) ||
      lead.telephone?.includes(searchTerm) ||
      lead.email?.toLowerCase().includes(searchLower)
    );
  });

  // Red flag logic for Télépro users
  const { filteredLeads, redFlagCount, hasBlockedNewLeads } = useMemo(() => {
    // Apply user sort to the search-filtered leads
    const applySorting = (list) => {
      return [...list].sort((a, b) => {
        let va, vb;
        switch (sortField) {
          case "date_reception": va = a.date_reception || a.created_at || ""; vb = b.date_reception || b.created_at || ""; break;
          case "nom": va = (a.nom || "").toLowerCase(); vb = (b.nom || "").toLowerCase(); break;
          case "ville": va = (a.ville || "").toLowerCase(); vb = (b.ville || "").toLowerCase(); break;
          case "telephone": va = a.telephone || ""; vb = b.telephone || ""; break;
          case "logement": va = a.logement || ""; vb = b.logement || ""; break;
          case "chauffage": va = a.systeme_chauffage || ""; vb = b.systeme_chauffage || ""; break;
          case "attribue": va = a.telepro_assigne || ""; vb = b.telepro_assigne || ""; break;
          case "statut": va = a.statut || ""; vb = b.statut || ""; break;
          default: va = a.created_at || ""; vb = b.created_at || "";
        }
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    };

    if (!isUserTelepro) {
      return { filteredLeads: applySorting(searchFilteredLeads), redFlagCount: 0, hasBlockedNewLeads: false };
    }

    // Compute red flag status for each lead
    const leadsWithFlags = searchFilteredLeads.map(lead => {
      // Red flag: status is "rappeler" or "ne_repond_pas" AND created/imported within last 7 days
      const isRedFlag = (lead.statut === "rappeler" || lead.statut === "ne_repond_pas") &&
        isWithinDays(lead.created_at, 7);
      // Today's new lead: imported/created today
      const isNewToday = isToday(lead.created_at) && lead.statut === "a_appeler";
      return { ...lead, _isRedFlag: isRedFlag, _isNewToday: isNewToday };
    });

    // Count unprocessed red flags
    const unprocessedRedFlags = leadsWithFlags.filter(l => l._isRedFlag).length;

    // If there are unprocessed red flags, hide today's new leads
    const visibleLeads = unprocessedRedFlags > 0
      ? leadsWithFlags.filter(l => !l._isNewToday)
      : leadsWithFlags;

    // Sort: red flags first, then apply user sort
    const sorted = applySorting([...visibleLeads]).sort((a, b) => {
      if (a._isRedFlag && !b._isRedFlag) return -1;
      if (!a._isRedFlag && b._isRedFlag) return 1;
      return 0; // preserve user sort otherwise
    });

    return {
      filteredLeads: sorted,
      redFlagCount: unprocessedRedFlags,
      hasBlockedNewLeads: unprocessedRedFlags > 0 && leadsWithFlags.some(l => l._isNewToday),
    };
  }, [searchFilteredLeads, isUserTelepro, sortField, sortDir]);

  if (loading) {
    return (
      <div className="p-6 md:p-12 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-muted rounded-xl"></div>
          ))}
        </div>
        <div className="h-96 bg-muted rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-2">
            Leads
          </h1>
          <p className="text-muted-foreground">
            {isUserTelepro 
              ? `Vos leads assignés - ${userName}` 
              : "Gestion des leads et suivi téléphonique"
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Import Excel - visible uniquement pour Admin et Renova */}
          {canUseBulkActions && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-import-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="rounded-xl"
                data-testid="import-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Importer Excel
              </Button>
            </>
          )}
          <Button
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            className="rounded-xl bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
            data-testid="create-lead-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau lead
          </Button>
        </div>
      </div>

      {/* Barre d'actions en masse - visible uniquement pour Admin et Renova */}
      {canUseBulkActions && selectedLeads.length > 0 && (
        <div className="flex items-center gap-3 mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl animate-fade-in">
          <CheckSquare className="w-5 h-5 text-primary" />
          <span className="font-medium text-sm">
            {selectedLeads.length} lead(s) sélectionné(s)
          </span>
          <div className="flex-1" />
          
          {/* Dropdown Attribution */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={bulkActionLoading}
                className="rounded-lg"
                data-testid="bulk-assign-btn"
              >
                {bulkActionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                Attribuer à
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {assignableUsers.map((user) => (
                <DropdownMenuItem
                  key={user}
                  onClick={() => handleBulkAssign(user)}
                  data-testid={`assign-to-${user.toLowerCase()}`}
                >
                  <UserCheck className="w-4 h-4 mr-2 text-muted-foreground" />
                  {user}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => handleBulkAssign("")}
                className="text-muted-foreground"
              >
                <UserX className="w-4 h-4 mr-2" />
                Retirer l'attribution
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Bouton Supprimer */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkDelete}
            disabled={bulkActionLoading}
            className="rounded-lg text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
            data-testid="bulk-delete-btn"
          >
            {bulkActionLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Supprimer
          </Button>
          
          {/* Bouton Annuler sélection */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedLeads([])}
            className="rounded-lg"
            data-testid="cancel-selection-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const count = stats[key] || 0;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
              className={cn(
                "p-4 rounded-xl border transition-all",
                statusFilter === key
                  ? `${config.bg} ${config.border} border-2`
                  : "bg-card border-border hover:border-primary/20"
              )}
              data-testid={`stat-${key}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={cn("w-4 h-4", config.color)} />
                <span className="text-xs font-medium text-muted-foreground">{config.label}</span>
              </div>
              <p className={cn("text-2xl font-bold", config.color)}>{count}</p>
            </button>
          );
        })}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom, ville, téléphone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="search-input"
          />
        </div>
        {statusFilter !== "all" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusFilter("all")}
            className="rounded-xl"
          >
            <X className="w-4 h-4 mr-2" />
            Effacer le filtre
          </Button>
        )}
        <p className="text-sm text-muted-foreground">
          {filteredLeads.length} lead{filteredLeads.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Red Flag Banner for Télépro */}
      {isUserTelepro && redFlagCount > 0 && (
        <div className="mb-4 p-4 rounded-xl border-2 border-red-300 bg-red-500/10 flex items-center gap-3" data-testid="red-flag-banner">
          <Flag className="w-5 h-5 text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              {redFlagCount} lead{redFlagCount > 1 ? "s" : ""} prioritaire{redFlagCount > 1 ? "s" : ""} à traiter
            </p>
            <p className="text-xs text-red-600">
              {hasBlockedNewLeads
                ? "Vous devez rappeler ces leads avant de pouvoir accéder aux nouveaux leads du jour."
                : "Ces leads sont en attente de rappel depuis moins de 7 jours."}
            </p>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="border border-border rounded-xl bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {/* Colonne checkbox - visible uniquement pour Admin et Renova */}
              {canUseBulkActions && (
                <TableHead className="w-12">
                  <button
                    onClick={handleSelectAll}
                    className="p-1 hover:bg-muted rounded"
                    data-testid="select-all-checkbox"
                  >
                    {selectedLeads.length === filteredLeads.length && filteredLeads.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-primary" />
                    ) : (
                      <Square className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                </TableHead>
              )}
              <SortTableHead label="Date" field="date_reception" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortTableHead label="Nom / Prénom" field="nom" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortTableHead label="Ville" field="ville" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortTableHead label="Téléphone" field="telephone" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortTableHead label="Logement" field="logement" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortTableHead label="Chauffage" field="chauffage" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              {/* Colonne Attribué à - visible pour voir les attributions */}
              {canSeeAttribution && (
                <SortTableHead label="Attribué à" field="attribue" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              )}
              <SortTableHead label="Statut" field="statut" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canUseBulkActions ? 11 : canSeeAttribution ? 9 : 8} className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-muted-foreground">Aucun lead trouvé</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Importez un fichier Excel ou créez un nouveau lead
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredLeads.map((lead) => {
                const statusConfig = STATUS_CONFIG[lead.statut] || STATUS_CONFIG.a_appeler;
                const StatusIcon = statusConfig.icon;
                const isSelected = selectedLeads.includes(lead.id);
                const isRedFlagged = lead._isRedFlag;
                return (
                  <TableRow 
                    key={lead.id}
                    className={cn(
                      "hover:bg-muted/30 cursor-pointer",
                      isSelected && "bg-primary/5",
                      isRedFlagged && "bg-red-500/10 border-l-4 border-l-red-400"
                    )}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    {/* Checkbox - visible uniquement pour Admin et Renova */}
                    {canUseBulkActions && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleSelectLead(lead.id)}
                          className="p-1 hover:bg-muted rounded"
                          data-testid={`select-lead-${lead.id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-primary" />
                          ) : (
                            <Square className="w-5 h-5 text-muted-foreground" />
                          )}
                        </button>
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        {isRedFlagged && <Flag className="w-3.5 h-3.5 text-red-500 shrink-0" data-testid={`red-flag-${lead.id}`} />}
                        {lead.date_reception || "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{lead.nom} {lead.prenom}</p>
                        {lead.email && (
                          <p className="text-xs text-muted-foreground">{lead.email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-muted-foreground" />
                        <span className="text-sm">{lead.ville || "-"}</span>
                        {lead.code_postal && (
                          <span className="text-xs text-muted-foreground">({lead.code_postal})</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {lead.telephone ? (
                        <a 
                          href={`tel:${lead.telephone}`} 
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Phone className="w-3 h-3" />
                          {lead.telephone}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {lead.logement || "-"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {lead.systeme_chauffage || "-"}
                    </TableCell>
                    {/* Colonne Attribué à - visible pour Admin/Renova/Administratif */}
                    {canSeeAttribution && (
                      <TableCell>
                        {lead.attribue_a ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/10 text-blue-700 border border-blue-500/30">
                            <UserCheck className="w-3 h-3" />
                            {lead.attribue_a}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">Non attribué</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border",
                              statusConfig.bg,
                              statusConfig.border,
                              statusConfig.color
                            )}
                            data-testid={`status-trigger-${lead.id}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                            const Icon = config.icon;
                            return (
                              <DropdownMenuItem
                                key={key}
                                onClick={() => handleStatusChange(lead.id, key)}
                                className="flex items-center gap-2"
                              >
                                <Icon className={cn("w-4 h-4", config.color)} />
                                {config.label}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          className="rounded-lg"
                          data-testid={`edit-lead-${lead.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteLead(lead.id)}
                          className="rounded-lg text-destructive hover:text-destructive"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Nouveau lead
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations du nouveau lead
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">Date de réception</Label>
              <Input
                type="date"
                value={formData.date_reception}
                onChange={(e) => setFormData({ ...formData, date_reception: e.target.value })}
                className="rounded-lg"
                data-testid="input-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select
                value={formData.statut}
                onValueChange={(value) => setFormData({ ...formData, statut: value })}
              >
                <SelectTrigger className="rounded-lg" data-testid="select-statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className={cn("w-4 h-4", config.color)} />
                          {config.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Nom de famille"
                className="rounded-lg"
                data-testid="input-nom"
              />
            </div>
            <div className="space-y-2">
              <Label>Prénom</Label>
              <Input
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                placeholder="Prénom"
                className="rounded-lg"
                data-testid="input-prenom"
              />
            </div>

            <div className="space-y-2">
              <Label>Ville</Label>
              <Input
                value={formData.ville}
                onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                placeholder="Ville"
                className="rounded-lg"
                data-testid="input-ville"
              />
            </div>
            <div className="space-y-2">
              <Label>Code postal</Label>
              <Input
                value={formData.code_postal}
                onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                placeholder="Code postal"
                className="rounded-lg"
                data-testid="input-cp"
              />
            </div>

            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                placeholder="06 XX XX XX XX"
                className="rounded-lg"
                data-testid="input-tel"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemple.com"
                className="rounded-lg"
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label>Type de logement</Label>
              <Select
                value={formData.logement}
                onValueChange={(value) => setFormData({ ...formData, logement: value })}
              >
                <SelectTrigger className="rounded-lg" data-testid="select-logement">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Maison">Maison</SelectItem>
                  <SelectItem value="Appartement">Appartement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ancienneté du logement</Label>
              <Select value={formData.anciennete_logement} onValueChange={(value) => setFormData({ ...formData, anciennete_logement: value })}>
                <SelectTrigger className="rounded-lg" data-testid="select-anciennete"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Moins de 2 ans">Moins de 2 ans</SelectItem>
                  <SelectItem value="2 à 15 ans">2 à 15 ans</SelectItem>
                  <SelectItem value="Plus de 15 ans">Plus de 15 ans</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Système de chauffage</Label>
              <Select value={formData.systeme_chauffage} onValueChange={(value) => setFormData({ ...formData, systeme_chauffage: value })}>
                <SelectTrigger className="rounded-lg" data-testid="select-chauffage"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Gaz">Gaz</SelectItem>
                  <SelectItem value="Fioul">Fioul</SelectItem>
                  <SelectItem value="Électrique">Électrique</SelectItem>
                  <SelectItem value="Bois">Bois</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Programme intéressé</Label>
              <Select value={formData.programme_interesse || ""} onValueChange={(value) => setFormData({ ...formData, programme_interesse: value })}>
                <SelectTrigger className="rounded-lg" data-testid="select-programme"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MPR">Programme MPR</SelectItem>
                  <SelectItem value="174">Programme 174</SelectItem>
                  <SelectItem value="cumule">Op. Cumulée</SelectItem>
                  <SelectItem value="sci">Dossier SCI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Revenu fiscal annuel</Label>
              <Input
                value={formData.revenu_fiscal}
                onChange={(e) => setFormData({ ...formData, revenu_fiscal: e.target.value })}
                placeholder="Ex: 25000€"
                className="rounded-lg"
                data-testid="input-revenu"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Nombre de personnes dans le foyer</Label>
              <div className="flex gap-2 items-center">
                <Select value={formData.personnes_foyer || ""} onValueChange={(value) => setFormData({ ...formData, personnes_foyer: value })}>
                  <SelectTrigger className="rounded-lg w-[120px]" data-testid="select-foyer"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {["1","2","3","4","5+"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                {formData.personnes_foyer === "5+" && (
                  <Input type="number" min="6" value={formData.personnes_foyer_custom || ""} onChange={(e) => setFormData({ ...formData, personnes_foyer_custom: e.target.value })} placeholder="Nombre" className="rounded-lg w-24" />
                )}
              </div>
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Commentaire</Label>
              <Textarea
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                placeholder="Notes sur le lead..."
                className="rounded-lg min-h-[80px]"
                data-testid="input-commentaire"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Commentaire Mendy</Label>
              <Textarea
                value={formData.commentaire_mendy}
                onChange={(e) => setFormData({ ...formData, commentaire_mendy: e.target.value })}
                placeholder="Notes Mendy..."
                className="rounded-lg min-h-[80px]"
                data-testid="input-commentaire-mendy"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetForm();
              }}
              className="rounded-lg"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={saving}
              className="rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
              data-testid="save-lead-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
